// src/pages/AdminPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import BadgeIcon from "@mui/icons-material/Badge";
import AddIcon from "@mui/icons-material/Add";
import BuildIcon from "@mui/icons-material/Build";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import DashboardLayout from "../layouts/DashboardLayout";
import { useAuth } from "../context/AuthContext";
import ENV from "../config/env";
import { getSetting, setSetting } from "../services/settingsService";
import {
  getSincronizarPlacasPreview,
  postSincronizarPlacasAplicar,
} from "../services/mantenimientoService";
import {
  listUsuariosTrs,
  createUsuarioTrs,
  updateUsuarioTrs,
  resetPasswordUsuarioTrs,
  uploadFotoUsuarioTrs,
} from "../services/usuariosTrsService";
import { listRoles, createRole } from "../services/rolesService";
import MenuManager from "../modules/menu/MenuManager";
import { comprimirImagen } from "../utils/comprimirImagen";
import AccesosAppTab from "../modules/appAccesos/AccesosAppTab";

const MAX_AVATAR_MB = 2;

const SUCURSALES = ["La Paz", "La Paz(El Alto)", "Santa Cruz", "Cochabamba", "Beni", "Pando", "Chuquisaca", "Potosi", "Tarija", "Oruro"];

const avatarSrc = (profilePicture) =>
  profilePicture
    ? profilePicture.startsWith("http")
      ? profilePicture
      : `${ENV.API_BASE_URL}${profilePicture}`
    : `${ENV.API_BASE_URL}/uploads/default-avatar.png`;

// Pestaña <-> segmento de ruta, para que /admin/menu y compañía sigan siendo URLs válidas.
const TAB_SLUGS = ["config", "usuarios", "roles", "menu", "herramientas", "accesos-app"];

function slugFromPathname(pathname) {
  const seg = pathname.replace(/^\/admin\/?/, "").split("/")[0];
  const idx = TAB_SLUGS.indexOf(seg);
  return idx >= 0 ? idx : 0;
}

function EditUserDialog({ open, onClose, usuario, onSaved, notify, roles }) {
  const [form, setForm] = useState({ nombre: "", email: "", role: "", sub_rol: "", sucursal: "", estado: "A" });
  const [newPassword, setNewPassword] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!usuario) return;
    setForm({
      nombre: usuario.nombre || "",
      email: usuario.email || "",
      role: usuario.role || "user",
      sub_rol: usuario.sub_rol || "",
      sucursal: usuario.sucursal || "",
      estado: usuario.estado || "A",
    });
    setNewPassword("");
    setSelectedFile(null);
    setPreview(null);
  }, [usuario]);

  if (!usuario) return null;

  const handleFileChange = async (e) => {
    const original = e.target.files[0];
    e.target.value = ""; // permite volver a elegir el mismo archivo
    if (!original) return;
    // Igual que en Mi cuenta: la foto se achica (máx. 512 px) y se guarda como WebP antes de subirla.
    const { file } = await comprimirImagen(original, { maxLado: 512, calidad: 0.85 });
    if (file.size > MAX_AVATAR_MB * 1024 * 1024) {
      notify(`La imagen no debe superar los ${MAX_AVATAR_MB} MB.`, "error");
      return;
    }
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUsuarioTrs(usuario.id, form);

      if (newPassword.trim()) {
        if (newPassword.trim().length < 8) {
          notify("La nueva contraseña debe tener al menos 8 caracteres.", "error");
          setSaving(false);
          return;
        }
        await resetPasswordUsuarioTrs(usuario.id, newPassword.trim());
      }

      if (selectedFile) {
        await uploadFotoUsuarioTrs(usuario.id, selectedFile);
      }

      notify("Usuario actualizado correctamente.", "success");
      onSaved();
      onClose();
    } catch (e) {
      notify(e?.response?.data?.error || e.message || "No se pudo guardar.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Editar usuario {usuario.username}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box sx={{ position: "relative", display: "inline-block" }}>
              <Avatar src={preview || avatarSrc(usuario.profile_picture)} sx={{ width: 96, height: 96 }} />
              <Tooltip title="Cambiar foto">
                <IconButton
                  component="label"
                  size="small"
                  sx={{ position: "absolute", bottom: 0, right: 0, bgcolor: "primary.main", color: "#fff", "&:hover": { bgcolor: "primary.dark" } }}
                >
                  <EditIcon fontSize="small" />
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <TextField
            label="Nombre"
            value={form.nombre}
            onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
            fullWidth
            size="small"
          />
          <TextField
            label="Email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            fullWidth
            size="small"
          />
          <TextField
            label="Sub-rol"
            value={form.sub_rol}
            onChange={(e) => setForm((f) => ({ ...f, sub_rol: e.target.value }))}
            fullWidth
            size="small"
            helperText='Permiso adicional de texto libre (ej: "inventario" habilita acciones extra en Centro de Equipos). Dejar vacío si no aplica.'
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Rol</InputLabel>
                <Select
                  label="Rol"
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                >
                  {roles.map((r) => (
                    <MenuItem key={r.nombre} value={r.nombre}>{r.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sucursal</InputLabel>
                <Select
                  label="Sucursal"
                  value={form.sucursal}
                  onChange={(e) => setForm((f) => ({ ...f, sucursal: e.target.value }))}
                >
                  {SUCURSALES.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Switch
                checked={form.estado === "A"}
                onChange={(e) => setForm((f) => ({ ...f, estado: e.target.checked ? "A" : "X" }))}
              />
            }
            label={form.estado === "A" ? "Activo" : "Inactivo"}
          />

          <Divider />

          <TextField
            label="Nueva contraseña (opcional)"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            helperText="Dejar vacío para no cambiarla. Mínimo 8 caracteres."
            fullWidth
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : "Guardar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const EMPTY_NEW_USER = { username: "", password: "", nombre: "", email: "", role: "user", sub_rol: "", sucursal: "", estado: "A" };

function CreateUserDialog({ open, onClose, onCreated, notify, roles }) {
  const [form, setForm] = useState(EMPTY_NEW_USER);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(EMPTY_NEW_USER);
  }, [open]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await createUsuarioTrs(form);
      notify(`Usuario "${form.username}" creado.`, "success");
      onCreated();
      onClose();
    } catch (e) {
      notify(e?.response?.data?.error || e.message || "No se pudo crear el usuario.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nuevo usuario</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info" sx={{ fontSize: 13 }}>
            Este usuario queda disponible únicamente en esta app (React/Node) — no en el sistema PHP viejo.
          </Alert>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Usuario (login)"
                value={form.username}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                helperText="minúsculas, sin espacios"
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Contraseña"
                type="password"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                helperText="mínimo 8 caracteres"
                fullWidth
                size="small"
              />
            </Grid>
          </Grid>

          <TextField
            label="Nombre completo"
            value={form.nombre}
            onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
            fullWidth
            size="small"
          />
          <TextField
            label="Email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            fullWidth
            size="small"
          />
          <TextField
            label="Sub-rol"
            value={form.sub_rol}
            onChange={(e) => setForm((f) => ({ ...f, sub_rol: e.target.value }))}
            helperText='opcional (ej: "inventario")'
            fullWidth
            size="small"
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Rol</InputLabel>
                <Select
                  label="Rol"
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                >
                  {roles.map((r) => (
                    <MenuItem key={r.nombre} value={r.nombre}>{r.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sucursal</InputLabel>
                <Select
                  label="Sucursal"
                  value={form.sucursal}
                  onChange={(e) => setForm((f) => ({ ...f, sucursal: e.target.value }))}
                >
                  {SUCURSALES.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Switch
                checked={form.estado === "A"}
                onChange={(e) => setForm((f) => ({ ...f, estado: e.target.checked ? "A" : "X" }))}
              />
            }
            label={form.estado === "A" ? "Activo" : "Inactivo"}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" onClick={handleCreate} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : "Crear usuario"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConfiguracionTab({ notify }) {
  const [helpEnabled, setHelpEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pwaInstallEnabled, setPwaInstallEnabled] = useState(true);
  const [loadingPwa, setLoadingPwa] = useState(true);

  useEffect(() => {
    getSetting("help_assistant_enabled")
      .then(({ data }) => setHelpEnabled(data?.valor === "1"))
      .catch(() => {})
      .finally(() => setLoading(false));

    getSetting("pwa_install_button_enabled")
      .then(({ data }) => setPwaInstallEnabled(data?.valor !== "0"))
      .catch(() => {})
      .finally(() => setLoadingPwa(false));
  }, []);

  const handleToggle = async (checked) => {
    setHelpEnabled(checked);
    try {
      await setSetting("help_assistant_enabled", checked ? "1" : "0");
      notify(`Asistente de ayuda ${checked ? "activado" : "desactivado"}.`, "success");
    } catch (e) {
      setHelpEnabled(!checked);
      notify("No se pudo cambiar la configuración.", "error");
    }
  };

  const handleTogglePwaInstall = async (checked) => {
    setPwaInstallEnabled(checked);
    try {
      await setSetting("pwa_install_button_enabled", checked ? "1" : "0");
      notify(`Botón de instalación ${checked ? "activado" : "desactivado"}.`, "success");
    } catch (e) {
      setPwaInstallEnabled(!checked);
      notify("No se pudo cambiar la configuración.", "error");
    }
  };

  return (
    <Stack spacing={1.5}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Asistente de ayuda
          </Typography>
          <FormControlLabel
            control={<Switch checked={helpEnabled} disabled={loading} onChange={(e) => handleToggle(e.target.checked)} />}
            label="Botón flotante de ayuda visible para todos los usuarios"
          />
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Botón de instalación (PWA)
          </Typography>
          <FormControlLabel
            control={(
              <Switch
                checked={pwaInstallEnabled}
                disabled={loadingPwa}
                onChange={(e) => handleTogglePwaInstall(e.target.checked)}
              />
            )}
            label="Botón flotante 'Instalar' visible para todos los usuarios"
          />
        </CardContent>
      </Card>

      <VersionAndroidCard notify={notify} />
    </Stack>
  );
}

// La app Android (TRS Sort) consulta estos dos valores en GET /api/settings/app-android/version
// cada vez que se abre; si su versionCode es menor al mínimo, queda bloqueada hasta actualizar.
function VersionAndroidCard({ notify }) {
  const [minimo, setMinimo] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [guardado, setGuardado] = useState({ minimo: "", mensaje: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      getSetting("android_min_version_code").then(({ data }) => data?.valor ?? "").catch(() => ""),
      getSetting("android_mensaje_actualizacion").then(({ data }) => data?.valor ?? "").catch(() => ""),
    ])
      .then(([min, msg]) => {
        setMinimo(min);
        setMensaje(msg);
        setGuardado({ minimo: min, mensaje: msg });
      })
      .finally(() => setLoading(false));
  }, []);

  const minimoLimpio = minimo.trim();
  const minimoValido = minimoLimpio === "" || /^[1-9]\d*$/.test(minimoLimpio);
  const sinCambios = minimoLimpio === guardado.minimo && mensaje.trim() === guardado.mensaje;

  const handleSave = async () => {
    if (!minimoValido) return;
    setSaving(true);
    try {
      // Vacío = no obliga a ninguna versión (el backend lo devuelve como null).
      await setSetting("android_min_version_code", minimoLimpio);
      await setSetting("android_mensaje_actualizacion", mensaje.trim());
      setGuardado({ minimo: minimoLimpio, mensaje: mensaje.trim() });
      notify(
        minimoLimpio
          ? `Versiones anteriores a ${minimoLimpio} quedarán bloqueadas hasta actualizar.`
          : "Actualización obligatoria desactivada.",
        "success"
      );
    } catch (e) {
      notify(e?.response?.data?.error || "No se pudo guardar la configuración.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          App Android: actualización obligatoria
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Si la versión instalada en el celular es menor al mínimo, la app se bloquea y pide actualizar desde
          Play Store. Deja el campo vacío para no obligar a nadie.
        </Typography>
        <Alert severity="warning" sx={{ fontSize: 13, mb: 2 }}>
          Sube el mínimo solo cuando esa versión ya esté publicada y disponible en Play Store; si no, los usuarios
          quedan bloqueados sin una versión que instalar.
        </Alert>
        <Stack spacing={2}>
          <TextField
            size="small"
            label="Versión mínima (versionCode)"
            placeholder="ej: 4"
            value={minimo}
            onChange={(e) => setMinimo(e.target.value)}
            disabled={loading || saving}
            error={!minimoValido}
            helperText={
              minimoValido
                ? "Número entero (versionCode de Play Console), no el nombre tipo 1.2"
                : "Debe ser un número entero mayor a 0"
            }
            inputProps={{ inputMode: "numeric" }}
            sx={{ maxWidth: 320 }}
          />
          <TextField
            size="small"
            label="Mensaje para el usuario (opcional)"
            placeholder="Esta versión ya no es compatible. Actualiza la app desde Play Store para seguir usándola."
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
            disabled={loading || saving}
            multiline
            minRows={2}
            fullWidth
          />
          <Box>
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={loading || saving || !minimoValido || sinCambios}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            >
              Guardar
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function HerramientasTab({ notify }) {
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handlePreview = async () => {
    setLoadingPreview(true);
    setResultado(null);
    try {
      const { data } = await getSincronizarPlacasPreview();
      setPreview(data);
    } catch (e) {
      notify(e?.response?.data?.error || "No se pudo calcular la vista previa.", "error");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleAplicar = async () => {
    setConfirmOpen(false);
    setApplying(true);
    try {
      const { data } = await postSincronizarPlacasAplicar();
      setResultado(data);
      notify(`Sincronización aplicada: ${data?.total ?? 0} vínculos procesados.`, "success");
      handlePreview();
    } catch (e) {
      notify(e?.response?.data?.error || "No se pudo aplicar la sincronización.", "error");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Sincronizar clientes por placa
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Busca módems que comparten placa con otro módem ya vinculado a un cliente,
            pero que nunca recibieron ese mismo vínculo (el bug que corregimos en el
            flujo de Adición). Solo agrega vínculos nuevos (como Secundario/YPFB) —
            nunca quita ni cambia el vínculo Principal de cada módem.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <Button variant="outlined" onClick={handlePreview} disabled={loadingPreview}>
              {loadingPreview ? <CircularProgress size={18} /> : "Vista previa"}
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={!preview?.total || applying}
              onClick={() => setConfirmOpen(true)}
            >
              {applying ? <CircularProgress size={18} /> : "Aplicar"}
            </Button>
          </Stack>

          {preview && preview.total === 0 && (
            <Alert severity="success">No hay casos pendientes.</Alert>
          )}

          {preview && preview.total > 0 && (
            <>
              <Alert severity="info" sx={{ mb: 1 }}>
                {preview.total} vínculo(s) por sincronizar.
              </Alert>
              <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Placa</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Cod. Servicio</TableCell>
                      <TableCell>Módem a vincular</TableCell>
                      <TableCell>Tipo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.casos.map((c, i) => (
                      <TableRow key={`${c.modem_a_vincular}-${c.cod_servicio_cliente}-${i}`}>
                        <TableCell>{c.placa}</TableCell>
                        <TableCell>{c.cliente}</TableCell>
                        <TableCell>{c.cod_servicio_cliente}</TableCell>
                        <TableCell>{c.modem_a_vincular}</TableCell>
                        <TableCell>{c.tipo_vinculacion}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {resultado && (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              Última aplicación: {resultado.total} vínculo(s) procesados.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmar sincronización</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Se van a crear/reactivar {preview?.total ?? 0} vínculo(s) en rel_modem_servicio.
            Esta acción modifica datos reales. ¿Continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={handleAplicar}>Aplicar</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function UsuariosTab({ notify, roles }) {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchUsuarios = useCallback(async (nombre = "") => {
    setLoading(true);
    try {
      const { data } = await listUsuariosTrs({ nombre, order: "asc" });
      setUsuarios(Array.isArray(data) ? data : []);
    } catch (e) {
      notify(e?.response?.data?.error || "No se pudo cargar la lista de usuarios.", "error");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Buscar por nombre..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fetchUsuarios(search)}
          sx={{ minWidth: 260 }}
        />
        <Button variant="contained" size="small" startIcon={<SearchIcon fontSize="small" />} onClick={() => fetchUsuarios(search)}>
          Buscar
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" color="success" size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => setCreating(true)}>
          Agregar usuario
        </Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Nombre</TableCell>
              <TableCell>Usuario</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell>Sub-rol</TableCell>
              <TableCell>Sucursal</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress size={24} sx={{ my: 2 }} />
                </TableCell>
              </TableRow>
            ) : usuarios.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 2 }}>
                    No se encontraron usuarios.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              usuarios.map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell sx={{ width: 44 }}>
                    <Avatar src={avatarSrc(u.profile_picture)} sx={{ width: 30, height: 30 }} />
                  </TableCell>
                  <TableCell>{u.nombre}</TableCell>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.role}</TableCell>
                  <TableCell>{u.sub_rol || "—"}</TableCell>
                  <TableCell>{u.sucursal}</TableCell>
                  <TableCell>
                    <Chip size="small" label={u.estado === "A" ? "Activo" : "Inactivo"} color={u.estado === "A" ? "success" : "default"} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => setEditing(u)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <EditUserDialog
        open={Boolean(editing)}
        usuario={editing}
        onClose={() => setEditing(null)}
        onSaved={() => fetchUsuarios(search)}
        notify={notify}
        roles={roles}
      />

      <CreateUserDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => fetchUsuarios(search)}
        notify={notify}
        roles={roles}
      />
    </Paper>
  );
}

function RolesTab({ roles, onRoleCreated, notify }) {
  const [nombre, setNombre] = useState("");
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    const nombreLimpio = nombre.trim().toLowerCase();
    const labelLimpio = label.trim();
    if (!nombreLimpio || !labelLimpio) {
      notify("Completa nombre interno y etiqueta.", "warning");
      return;
    }

    setCreating(true);
    try {
      await createRole(nombreLimpio, labelLimpio);
      notify(`Rol "${labelLimpio}" creado.`, "success");
      setNombre("");
      setLabel("");
      onRoleCreated();
    } catch (e) {
      notify(e?.response?.data?.error || e.message || "No se pudo crear el rol.", "error");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info" sx={{ fontSize: 13 }}>
        Crear un rol acá solo lo hace disponible para asignarlo a usuarios y para elegirlo en el módulo "Menú"
        (qué secciones ve). No le otorga automáticamente permisos especiales en ninguna pantalla — eso sigue
        requiriendo cambios de código puntuales donde haga falta.
      </Alert>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Nuevo rol</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "flex-start" }}>
          <TextField
            size="small"
            label="Nombre interno"
            placeholder="ej: supervisor"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            helperText="minúsculas, sin espacios"
            fullWidth
          />
          <TextField
            size="small"
            label="Etiqueta visible"
            placeholder="ej: Supervisor"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            helperText="cómo se ve en pantalla"
            fullWidth
          />
          <Button
            variant="contained"
            size="small"
            startIcon={creating ? <CircularProgress size={14} color="inherit" /> : <AddIcon fontSize="small" />}
            onClick={handleCreate}
            disabled={creating}
            sx={{ minHeight: 40, height: 40, flexShrink: 0 }}
          >
            Crear
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Roles existentes</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre interno</TableCell>
                <TableCell>Etiqueta visible</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 1 }}>
                      No hay roles todavía.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((r) => (
                  <TableRow key={r.nombre} hover>
                    <TableCell>
                      <Chip size="small" label={r.nombre} variant="outlined" />
                    </TableCell>
                    <TableCell>{r.label}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
}

const TAB_META = [
  { label: "Configuración", icon: <SettingsIcon fontSize="small" /> },
  { label: "Usuarios", icon: <PeopleIcon fontSize="small" /> },
  { label: "Roles", icon: <BadgeIcon fontSize="small" /> },
  { label: "Menú", icon: <AccountTreeIcon fontSize="small" /> },
  { label: "Herramientas", icon: <BuildIcon fontSize="small" /> },
  { label: "Accesos app", icon: <PhoneAndroidIcon fontSize="small" /> },
];

export default function AdminPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const location = useLocation();
  const navigate = useNavigate();

  const tab = useMemo(() => slugFromPathname(location.pathname), [location.pathname]);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const notify = (message, severity = "success") => setSnack({ open: true, message, severity });

  const [roles, setRoles] = useState([]);
  const fetchRoles = useCallback(async () => {
    try {
      const { data } = await listRoles();
      setRoles(Array.isArray(data) ? data : []);
    } catch (e) {
      notify("No se pudo cargar la lista de roles.", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user?.role === "admin") fetchRoles();
  }, [user, fetchRoles]);

  const handleChangeTab = (_e, value) => {
    navigate(`/admin/${TAB_SLUGS[value]}`);
  };

  if (user === null) {
    return (
      <DashboardLayout>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress size={24} />
        </Box>
      </DashboardLayout>
    );
  }

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <Alert severity="warning" sx={{ mt: 2 }}>
          No tienes permisos para ver esta página. Solo usuarios con rol "admin" pueden acceder a Administración.
        </Alert>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Typography variant="h6" sx={{ mb: 1 }}>Administración</Typography>

      <Paper variant="outlined" sx={{ mb: 1.5 }}>
        <Tabs value={tab} onChange={handleChangeTab} variant="scrollable" scrollButtons="auto">
          {TAB_META.map((t) => (
            <Tab key={t.label} icon={t.icon} iconPosition="start" label={t.label} sx={{ minHeight: 44, textTransform: "none" }} />
          ))}
        </Tabs>
      </Paper>

      {tab === 0 && <ConfiguracionTab notify={notify} />}
      {tab === 1 && <UsuariosTab notify={notify} roles={roles} />}
      {tab === 2 && <RolesTab roles={roles} onRoleCreated={fetchRoles} notify={notify} />}
      {tab === 3 && <MenuManager embedded />}
      {tab === 4 && <HerramientasTab notify={notify} />}
      {tab === 5 && <AccesosAppTab notify={notify} />}

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
